import { useAttachmentsUploadManager } from '@components'

import { Button, ButtonsGroup, Flex, Modal, ModalProps, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentsPicker } from '@features/invoices/elements/AttachmentsPicker'

import { useSendAsEmailForm } from '../../../../contexts/sendAsEmailFormContext'
import { useTrackSendAsEmail } from '../../../../hooks/useTrackSendAsEmail'

type SendAsEmailModalFooterProps = Pick<ModalProps, 'id'>

export const SendAsEmailModalFooter = ({ id }: SendAsEmailModalFooterProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeSendAsEmailModal } = useModal(id)
  const { isLoading, isSubmitting, submitForm, sendAsEmailForm, validate } = useSendAsEmailForm()
  const { trackSendAsEmail } = useTrackSendAsEmail()
  const { isDeleting: isAttachmentsDeleting, isUploading: isAttachmentsUploading } = useAttachmentsUploadManager()

  const { watch } = sendAsEmailForm
  const isFooterActionButtonDisabled = isLoading || isSubmitting || isAttachmentsDeleting || isAttachmentsUploading

  const handleCancelButtonClick = useCallback(() => {
    closeSendAsEmailModal()
  }, [closeSendAsEmailModal])

  const handleSendButtonClick = useCallback(async () => {
    const emailRecipients = watch('emailRecipients')
    trackSendAsEmail('Send now clicked', {
      recipientCount: emailRecipients?.length || 0,
    })
    const isFormValid = await validate()

    if (isFormValid) {
      submitForm()
    }
  }, [submitForm, trackSendAsEmail, validate, watch])

  useEffect(() => {
    return () => {
      closeSendAsEmailModal()
    }
  }, [closeSendAsEmailModal])

  return (
    <Modal.Footer>
      <Flex alignItems="center" flexGrow={1} justifyContent="space-between">
        <AttachmentsPicker />
        <ButtonsGroup>
          <Button disabled={isFooterActionButtonDisabled} onClick={handleCancelButtonClick} variant="text">
            {t('cancel')}
          </Button>
          <Button disabled={isFooterActionButtonDisabled} loading={isSubmitting} onClick={handleSendButtonClick}>
            {t('external_invoices.send_as_email.modal.action.send_now')}
          </Button>
        </ButtonsGroup>
      </Flex>
    </Modal.Footer>
  )
}
